import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { COMPANY } from "@/i18n";

/**
 * SEO component that outputs page-level meta:
 *   - self-referencing canonical (absolute URL)
 *   - <title>
 *   - description
 *   - Open Graph (type, title, description, image, url, locale)
 *   - Twitter card
 *   - alternate hreflang for SR/EN
 *
 * Base URL priority:
 *   1. props.baseUrl
 *   2. window.location.origin (runtime — works on any custom domain)
 *   3. COMPANY.siteUrl fallback
 */
export const SEO = ({
  title,
  description,
  image = COMPANY.logo,
  type = "website",
  lang = "sr",
  baseUrl,
}) => {
  const location = useLocation();
  const origin =
    baseUrl ||
    (typeof window !== "undefined" && window.location && window.location.origin) ||
    COMPANY.siteUrl ||
    "";
  const url = `${origin}${location.pathname}`;
  const ogLocale = lang === "en" ? "en_US" : "sr_RS";
  const altLocale = lang === "en" ? "sr_RS" : "en_US";

  return (
    <Helmet prioritizeSeoTags>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={COMPANY.name} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={altLocale} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={COMPANY.name} />
      <meta property="og:url" content={url} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* hreflang alternates */}
      <link rel="alternate" hrefLang="sr" href={url} />
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />
    </Helmet>
  );
};
